import { IField, getJsonKeyFromField } from "@eventr/common";
import { ValidatorProvider } from "@eventr/common-storybook";
import { ReactNode } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { useBoundStore } from "../../state/bound.store";
import { EditSectionProvider } from "./editSectionProvider";

const ValidatorProviderRoute = ({ children }: { children: ReactNode }) => {
  const { fields } = useBoundStore(
    useShallow((s) => {
      return {
        fields: s.fields,
      };
    }),
  );

  const getFieldByIdFromFields = (fields: IField[], id: number) => {
    return fields?.find((field) => Number.parseInt(field.id?.toString()) === Number.parseInt(id?.toString()));
  };

  const getFieldById = (id: number) => {
    return getFieldByIdFromFields(fields, id);
  };

  const buildPathToField = (fieldId: number) => {
    const path: string[] = [];
    const field = getFieldById(fieldId);
    if (!field) return path;
    const fieldJson = getJsonKeyFromField(field);
    path.push(fieldJson);

    let parent: number | undefined = field?.parent;
    while (parent) {
      const json = getJsonKeyFromField(getFieldById(parent)!);

      path.push(json);
      parent = getFieldById(parent)?.parent;
    }
    return path.reverse();
  };
  return <ValidatorProvider buildPathToField={buildPathToField}>{children}</ValidatorProvider>;
};

/**
 * Layout route for the section-scoped family (section/sectionItem/sectionItemPage/
 * sectionItemPageItem): wraps their shared parent Route in ValidatorProviderRoute +
 * EditSectionProvider, keyed on sectionId, with the leaves nested underneath via
 * <Outlet/>. One provider instance per section, shared across all of that section's
 * leaves. A booking edited on the sectionItemPageItem leaf and a Done back to the
 * sectionItemPage list must see the same in-memory `values`, not two independently
 * mounted providers that each reload fresh from the store.
 *
 * React Router won't remount a matched route element on a param-only change, so
 * the key on EditSectionProvider is required: without it, the per-section state
 * (values, sortedFields, ...) would carry over stale when navigating from one
 * section straight to another.
 */
export const EditSectionRoutes = () => {
  const { sectionId } = useParams<{ sectionId: string }>();
  return (
    <ValidatorProviderRoute>
      <EditSectionProvider key={sectionId}>
        <Outlet />
      </EditSectionProvider>
    </ValidatorProviderRoute>
  );
};
